// Format aggregation to readable format
export const formatAggregation = (agg) => {
  if (!agg) return agg;
  return agg.replace('_', ' ');
};

// Format time range to human readable format
export const formatTimeRange = (startTime, endTime) => {
  const formatDateTime = (timeStr) => {
    if (!timeStr) return 'N/A';
    try {
      const date = new Date(timeStr);
      return date.toLocaleString([], { 
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return timeStr;
    }
  };
  
  const start = formatDateTime(startTime);
  const end = formatDateTime(endTime);
  return `${start} - ${end}`;
};

// Format short display time for charts
export const formatDisplayTime = (startTime, aggregation, timeRangeType) => {
  if (!startTime) return 'N/A';
  
  try {
    const date = new Date(startTime);
    
    if (timeRangeType === 'week' || timeRangeType === 'month') {
      if (timeRangeType === 'week') {
        return date.toLocaleDateString('en-US', { 
          weekday: 'short',
          month: 'short',
          day: 'numeric'
        });
      } else {
        return date.toLocaleDateString('en-US', { 
          month: 'short',
          day: 'numeric'
        });
      }
    }
    
    // For today view
    const timeStr = date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
    
    // For 15_min aggregations, show start time only
    if (aggregation === '15_min' || aggregation === '15 min') {
      return timeStr;
    }
    
    // For other aggregations, calculate end time and show range
    const endTime = new Date(date.getTime() + 15 * 60000); // Add 15 minutes
    const endTimeStr = endTime.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
    
    return `${timeStr} - ${endTimeStr}`;
  } catch (error) {
    return startTime;
  }
};

// Format metric name for display
export const formatMetricName = (metricKey) => {
  const metricNames = {
    'respiratory_rate': 'Respiratory Rate',
    'heart_rate': 'Heart Rate',
    'active_energy': 'Active Energy',
    'hrv': 'HRV'
  };
  return metricNames[metricKey] || metricKey;
};

// Format date for display in header
export const formatDateRange = (fromDate, toDate) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString([], {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return dateStr;
    }
  };
  
  return `${formatDate(fromDate)} to ${formatDate(toDate)}`;
};

// Get display text for time range
export const getTimeRangeDisplayText = (timeRangeType) => {
  switch(timeRangeType) {
    case 'today':
      return 'Today';
    case 'week':
      return 'Last 7 Days';
    case 'month':
      return 'Last 30 Days';
    case 'custom':
      return 'Custom Range';
    default:
      return 'Select Range';
  }
};

// Format time for display based on time range type and aggregation
export const formatChartDisplayTime = (timestamp, timeRangeType, aggregation) => {
  if (!timestamp) return 'N/A';
  
  try {
    const date = new Date(timestamp);
    
    if (timeRangeType === 'week' || timeRangeType === 'month') {
      if (timeRangeType === 'week') {
        return date.toLocaleDateString('en-US', { 
          weekday: 'short',
          month: 'short',
          day: 'numeric'
        });
      } else {
        return date.toLocaleDateString('en-US', { 
          month: 'short',
          day: 'numeric'
        });
      }
    }
    
    // For today view with 2-hour aggregation
    if (aggregation === '2_hour' || aggregation === '2 hour') {
      const hour = date.getHours();
      const nextHour = (hour + 2) % 24;
      const period = hour < 12 ? 'AM' : 'PM';
      const nextPeriod = nextHour < 12 ? 'AM' : 'PM';
      
      const formatHour = (h) => {
        const displayHour = h % 12 || 12;
        return `${displayHour}${h < 12 ? 'AM' : 'PM'}`;
      };
      
      return `${formatHour(hour)} - ${formatHour(nextHour)}`;
    }
    
    // For today view with 15-minute intervals (fallback)
    return date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  } catch (error) {
    return timestamp;
  }
};

// Calculate 2-hour averages from 15-minute data
export const calculate2HourAverages = (chartData, currentTime = new Date()) => {
  if (!chartData || chartData.length === 0) return [];
  
  const twoHourData = [];
  let currentGroup = [];
  let groupStartTime = null;
  
  // Group data into 2-hour intervals (8 x 15-minute intervals = 2 hours)
  chartData.forEach((item, index) => {
    if (index % 8 === 0) {
      // Start new 2-hour group
      if (currentGroup.length > 0) {
        const block = calculateAverageForGroup(currentGroup, groupStartTime, currentTime);
        // Only add block if it has started (start time is in the past)
        const blockStart = new Date(block.startTime).getTime();
        if (blockStart <= currentTime.getTime()) {
          twoHourData.push(block);
        }
      }
      currentGroup = [item];
      groupStartTime = item.startTime;
    } else {
      currentGroup.push(item);
    }
  });
  
  // Add the last group if it exists and has started
  if (currentGroup.length > 0) {
    const lastBlock = calculateAverageForGroup(currentGroup, groupStartTime, currentTime);
    const blockStart = new Date(lastBlock.startTime).getTime();
    if (blockStart <= currentTime.getTime()) {
      twoHourData.push(lastBlock);
    }
  }
  
  return twoHourData;
};

// Helper function to calculate averages for a group
const calculateAverageForGroup = (group, startTime, currentTime = new Date()) => {
  const validItems = group.filter(item => !item.isEmptyData);
  
  if (validItems.length === 0) {
    // All empty data in this group
    const endTime = new Date(new Date(startTime).getTime() + 2 * 60 * 60 * 1000);
    
    // Check if this is a current/active time block
    const isCurrentBlock = currentTime.getTime() >= new Date(startTime).getTime() && 
                          currentTime.getTime() < endTime.getTime();
    
    return {
      batchId: `2hr_${startTime}`,
      intervalLabel: '2_hour',
      timeRange: `${formatTimeRange(startTime, endTime.toISOString())}`,
      startTime: startTime,
      endTime: endTime.toISOString(),
      timestamp: new Date(startTime).getTime() / 1000,
      displayTime: formatChartDisplayTime(startTime, 'today', '2_hour'),
      aggregation: '2 Hour Average',
      respiratory_rate: 0,
      heart_rate: 0,
      active_energy: 0,
      hrv: 0,
      dataPoints: { respiratory: 0, heart: 0, energy: 0, hrv: 0 },
      isAverage: true,
      isConsecutive: false,
      batchInfo: isCurrentBlock ? 'Current Time Block' : 'No Data',
      continuityNote: 'Missing data',
      isEmptyData: true,
      is2HourAggregate: true,
      isCurrentTimeBlock: isCurrentBlock
    };
  }
  
  // Calculate averages and format to 2 decimal places
  const respiratory_sum = validItems.reduce((sum, item) => sum + (item.respiratory_rate || 0), 0);
  const respiratory_avg = parseFloat((respiratory_sum / validItems.length).toFixed(2));
  
  const heart_sum = validItems.reduce((sum, item) => sum + (item.heart_rate || 0), 0);
  const heart_avg = parseFloat((heart_sum / validItems.length).toFixed(2));
  
  const energy_sum = parseFloat(validItems.reduce((sum, item) => sum + (item.active_energy || 0), 0).toFixed(2));
  
  const hrv_sum = validItems.reduce((sum, item) => sum + (item.hrv || 0), 0);
  const hrv_avg = parseFloat((hrv_sum / validItems.length).toFixed(2));
  
  // Sum of data points (these are counts, so keep as integers)
  const dataPoints = {
    respiratory: validItems.reduce((sum, item) => sum + (item.dataPoints?.respiratory || 0), 0),
    heart: validItems.reduce((sum, item) => sum + (item.dataPoints?.heart || 0), 0),
    energy: validItems.reduce((sum, item) => sum + (item.dataPoints?.energy || 0), 0),
    hrv: validItems.reduce((sum, item) => sum + (item.dataPoints?.hrv || 0), 0)
  };
  const endTime = new Date(new Date(startTime).getTime() + 2 * 60 * 60 * 1000);
  
  // Check if this is a current/active time block
  const isCurrentBlock = currentTime.getTime() >= new Date(startTime).getTime() && 
                        currentTime.getTime() < endTime.getTime();
  
  return {
    batchId: `2hr_${startTime}`,
    intervalLabel: '2_hour',
    timeRange: formatTimeRange(startTime, endTime.toISOString()),
    startTime: startTime,
    endTime: endTime.toISOString(),
    timestamp: new Date(startTime).getTime() / 1000,
    displayTime: formatChartDisplayTime(startTime, 'today', '2_hour'),
    aggregation: '2 Hour Average',
    respiratory_rate: respiratory_avg,
    heart_rate: heart_avg,
    active_energy: energy_sum,
    hrv: hrv_avg,
    dataPoints: dataPoints,
    isAverage: true,
    isConsecutive: false,
    batchInfo: isCurrentBlock ? `Current (${new Date(startTime).getHours()}:00-${endTime.getHours()}:00)` : 
               `2-Hour Block ${new Date(startTime).getHours()}:00`,
    continuityNote: '2-hour aggregate',
    isEmptyData: false,
    is2HourAggregate: true,
    isCurrentTimeBlock: isCurrentBlock
  };
};